import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { ReportTemplate, useDeleteReportTemplate } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';

interface ReportTemplateDeleteProps {
  reportTemplate: ReportTemplate;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReportTemplateDelete = ({
  reportTemplate,
  showDialog,
  setShowDialog,
}: ReportTemplateDeleteProps) => {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate } = useDeleteReportTemplate();
  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();

    setIsSubmitting(true);
    const templateName = reportTemplate.tplName;

    if (!reportTemplate.tplUuid) {
      toast({
        className: 'delete-report-template-error-toast',
        title: messages.genericToastTitleError,
        description: messages.reportsDeleteTemplateFailedNoIdMessage.replace(
          '{label}',
          templateName
        ),
        variant: 'destructive',
      });
      setIsSubmitting(false);
      setShowDialog(false);
      return;
    }

    mutate(reportTemplate.tplUuid, {
      onSuccess: () => {
        toast({
          className: 'delete-report-template-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsDeleteTemplateSuccessMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
      onError: (error: Error) => {
        let text = messages.reportsDeleteTemplateFailedGenericMessage.replace(
          '{label}',
          templateName
        );

        if (error.message.includes('access denied')) {
          text =
            messages.reportsDeleteTemplateFailedMissingPermissionsMessage.replace(
              '{label}',
              templateName
            );
        }

        toast({
          className: 'delete-report-template-error-toast',
          title: messages.genericToastTitleError,
          description: text,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="delete-report-template-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.reportsDeleteTemplateTitle}</DialogTitle>
          <DialogDescription>
            {messages.reportsDeleteTemplateWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.reportsDeleteTemplateNameLabel}
            </Label>
            <Label htmlFor="report-template-name">
              {reportTemplate.tplName}
            </Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              id="report-template-confirm-delete-button"
              onClick={(event) => onDelete(event)}
              variant="destructive"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
